import type {
  AttendanceRange,
  Baby,
  ChipStatus,
  Dose,
  DoseStatus,
  DueItem,
  Flag,
  Mother,
  NewPatientInput,
  NextItem,
  PatientDetail,
  PatientRow,
  Role,
  ScannedCard,
  TimelineEntry,
} from '../types';
import { collection, deleteDoc, deleteField, doc, getDoc, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { addDays, dayDiff, isToday, parseDate, startOfToday, statusByDiff, toIsoDate } from '../lib/dates';
import { db } from '../lib/firebase';
import { babies, doses, dosesForBaby, flags, milestoneMarks, mothers } from './mockData';
import { milestoneItems, sourceOf, trackedSince } from './milestones';
import { respond } from './respond';
import { addAudit, type Actor } from './auditService';
import { isReal, loadClinic, loadTranscript, realStatus, type ClinicData } from './store';
import { scope } from './scope';
import { paperConsentVersion } from './consent';

const src = sourceOf;
const babyOf = (c: ClinicData, motherId: string) => c.babies.find((b) => b.motherId === motherId);

function beforeJoining(c: ClinicData, m: Mother): TimelineEntry[] {
  const since = trackedSince(m);
  const baby = babyOf(c, m.id);
  const all = m.stage === 'pregnancy' || !baby
    ? milestoneItems(m, true, src(c)).map((i) => ({ id: i.id, kind: i.kind, name: i.name, date: i.date }))
    : c.doses.filter((d) => d.babyId === baby.id).map((d) => ({ id: d.id, kind: 'dose' as const, name: d.name, date: d.dueDate }));
  return all.filter((i) => i.date < since).map((i) => ({ id: i.id, kind: i.kind, label: i.name, date: i.date, status: 'before_joining' }));
}

function itemsFor(c: ClinicData, m: Mother): DueItem[] {
  const baby = babyOf(c, m.id);
  if (m.stage === 'pregnancy' || !baby) return milestoneItems(m, false, src(c));
  const since = trackedSince(m);
  return c.doses
    .filter((d) => d.babyId === baby.id && d.dueDate >= since)
    .map((d): DueItem => ({ id: d.id, kind: 'dose', mother: m, baby, name: d.name, date: d.dueDate, status: d.status }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

// The first not-attended item is always next, so a missed dose stays next until attended.
function nextFor(m: Mother, items: DueItem[]): NextItem | undefined {
  const n = items.find((i) => i.status !== 'attended');
  if (n) return { id: n.id, kind: n.kind, name: n.name, date: n.date, status: n.status };
  return m.stage === 'pregnancy' && m.edd ? { kind: 'delivery', name: '', date: m.edd, status: 'upcoming' } : undefined;
}

function chipFor(c: ClinicData, m: Mother): ChipStatus | null {
  if (m.optedOut) return 'opted_out';
  const last = c.calls.filter((x) => x.motherId === m.id).sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
  if (!last) return null;
  if ((last.outcome === 'no_answer' || last.outcome === 'failed') && !isToday(last.startedAt)) return 'retry_scheduled';
  return last.outcome;
}

export async function getPatientRows(facilityId: string): Promise<PatientRow[]> {
  const c = await loadClinic(facilityId);
  const today = toIsoDate(startOfToday());
  const rows = c.mothers
    .map((m): PatientRow => {
      const items = itemsFor(c, m);
      const past = items.filter((i) => i.date <= today && i.status !== 'upcoming');
      return { mother: m, baby: babyOf(c, m.id), next: nextFor(m, items), chip: chipFor(c, m), attended: past[past.length - 1]?.status === 'attended' };
    })
    .sort((a, b) => a.mother.name.localeCompare(b.mother.name));
  return c.real ? rows : respond(rows);
}

// Staff never get transcripts: in real mode the database refuses them (firestore.rules).
export async function getPatientDetail(motherId: string, role: Role): Promise<PatientDetail> {
  if (role === 'super_admin') throw new Error('Patient not found');
  const facilityId = isReal() ? scope().facilityId : mothers.find((x) => x.id === motherId)?.facilityId ?? '';
  const c = await loadClinic(facilityId);
  const m = c.mothers.find((x) => x.id === motherId);
  if (!m) throw new Error('Patient not found');
  const baby = babyOf(c, m.id);
  const items = [...beforeJoining(c, m), ...itemsFor(c, m).map((i): TimelineEntry => ({ id: i.id, kind: i.kind, label: i.name, date: i.date, status: i.status }))];
  const timeline: TimelineEntry[] =
    m.stage === 'pregnancy'
      ? [
          ...[{ id: 'registered', kind: 'registered', date: m.consent.date, status: 'attended' } as TimelineEntry, ...items].sort((a, b) => (a.date ?? '').localeCompare(b.date ?? '')),
          { id: 'delivery', kind: 'delivery', date: m.edd, status: 'upcoming' },
        ]
      : [{ id: 'pregnancy', kind: 'pregnancy', status: 'attended' }, { id: 'delivery', kind: 'delivery', date: baby?.dob, status: 'attended' }, ...items];
  const canSeeTranscript = role === 'doctor' || role === 'facility_admin';
  const motherCalls = c.calls.filter((x) => x.motherId === m.id).sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  const withTranscripts = canSeeTranscript && c.real
    ? await Promise.all(motherCalls.map(async (x) => ({ ...x, transcript: await loadTranscript(facilityId, x.id) })))
    : motherCalls.map((x) => (canSeeTranscript ? x : { ...x, transcript: undefined }));
  const detail: PatientDetail = {
    mother: m,
    baby,
    timeline,
    next: nextFor(m, itemsFor(c, m)),
    calls: withTranscripts,
    flags: c.flags.filter((f) => f.motherId === m.id).sort((a, b) => b.raisedAt.localeCompare(a.raisedAt)),
  };
  return c.real ? detail : respond(detail);
}

function datesFor(input: NewPatientInput) {
  let { lmp, edd } = input;
  if (input.stage === 'pregnancy') {
    if (!lmp && edd) lmp = toIsoDate(addDays(parseDate(edd), -280));
    if (lmp && !edd) edd = toIsoDate(addDays(parseDate(lmp), 280));
  }
  return { lmp, edd };
}

// Undefined values are not allowed in Firestore.
const defined = <T extends object>(o: T) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;

// Consent on a new record (CLAUDE.md rule 19; firestore.rules allow only pending, or yes + paper).
// The verbal tick alone keeps her pending; a signed paper form makes it yes straight away.
function consentOnCreate(input: NewPatientInput): Pick<Mother, 'consentStatus' | 'consentChannel' | 'consentAt' | 'consentTextVersion'> {
  if (!input.paperConsent) return { consentStatus: 'pending' };
  return { consentStatus: 'yes', consentChannel: 'paper', consentAt: new Date().toISOString(), consentTextVersion: input.consentTextVersion ?? paperConsentVersion() };
}

export async function createPatient(input: NewPatientInput): Promise<Mother> {
  if (!input.consent.given || !input.consent.date) throw new Error('Consent is required before saving a patient');
  const { lmp, edd } = datesFor(input);

  if (isReal()) {
    const fid = input.facilityId;
    const ref = doc(collection(db!, 'facilities', fid, 'mothers'));
    const id = ref.id;
    const mother: Mother = defined({
      id,
      facilityId: fid,
      name: input.name.trim(),
      phone: input.phone,
      stage: input.stage,
      lmp: input.stage === 'pregnancy' ? lmp : undefined,
      edd: input.stage === 'pregnancy' ? edd : undefined,
      consent: input.consent,
      ...consentOnCreate(input),
      callLanguage: input.callLanguage,
      usualAnswerer: input.usualAnswerer,
      ...(input.minor ? { minor: true, guardian: input.guardian } : {}),
      optedOut: false,
      highRisk: false,
      uhid: `UH-${id.slice(0, 6).toUpperCase()}`,
    });
    const batch = writeBatch(db!);
    batch.set(ref, mother);
    if (input.stage === 'baby' && input.dob) {
      const { protocol } = await loadClinic(fid);
      const baby: Baby = { id: `b-${id}`, motherId: id, facilityId: fid, dob: input.dob };
      batch.set(doc(db!, 'facilities', fid, 'babies', baby.id), baby);
      for (const step of protocol.filter((p) => p.stage === 'baby')) {
        const due = toIsoDate(addDays(parseDate(baby.dob), (step.ageWeeks ?? 0) * 7));
        const dose: Dose = { id: `${baby.id}-${step.id}`, babyId: baby.id, facilityId: fid, name: step.name, dueDate: due, status: realStatus(undefined, due) };
        batch.set(doc(db!, 'facilities', fid, 'doses', dose.id), defined(dose));
      }
    }
    await batch.commit();
    addAudit(scope().actor, 'patient.created', `Patient ${id}`, fid, input.stage);
    return mother;
  }

  const id = `m-${Date.now()}`;
  const mother: Mother = {
    id,
    facilityId: input.facilityId,
    name: input.name.trim(),
    phone: input.phone,
    stage: input.stage,
    lmp: input.stage === 'pregnancy' ? lmp : undefined,
    edd: input.stage === 'pregnancy' ? edd : undefined,
    consent: input.consent,
    ...consentOnCreate(input),
    callLanguage: input.callLanguage,
    usualAnswerer: input.usualAnswerer,
    ...(input.minor ? { minor: true, guardian: input.guardian } : {}),
    optedOut: false,
    highRisk: false,
    uhid: `${input.facilityId === 'sharma-meerut' ? 'SHM' : 'GMC'}-${1001 + mothers.length}`,
  };
  mothers.push(mother);
  if (input.stage === 'baby' && input.dob) {
    const baby = { id: `b-${id}`, motherId: id, facilityId: input.facilityId, dob: input.dob };
    babies.push(baby);
    doses.push(...dosesForBaby(baby));
  }
  return respond(mother);
}

export type OptOutReason = 'mother_asked' | 'wrong_number' | 'other';

export async function setOptOut(motherId: string, optedOut: boolean, actor?: Actor, reason?: OptOutReason): Promise<Mother> {
  if (optedOut && !reason) throw new Error('A reason is required to opt out');
  // Stopping also sets her consent to "no" (rule 19). Turning calls back on never restores consent by itself:
  // a mother whose status is "no" restarts only with a new yes (e.g. a signed paper form, recordPaperConsent).
  const stop = { optedOut: true, optOutReason: reason, consentStatus: 'no' as const, consentAt: new Date().toISOString() };
  if (isReal()) {
    const fid = scope().facilityId;
    await updateDoc(doc(db!, 'facilities', fid, 'mothers', motherId), optedOut ? stop : { optedOut: false });
    addAudit(actor, optedOut ? 'patient.opted_out' : 'patient.opted_in', `Patient ${motherId}`, fid, reason);
    const c = await loadClinic(fid);
    return c.mothers.find((x) => x.id === motherId)!;
  }
  const m = mothers.find((x) => x.id === motherId);
  if (!m) throw new Error('Patient not found');
  if (optedOut) Object.assign(m, stop);
  else m.optedOut = false;
  addAudit(actor, optedOut ? 'patient.opted_out' : 'patient.opted_in', `Patient ${m.id}`, m.facilityId, reason);
  return respond(m);
}

/** Reception saw her signed paper consent form: status yes, channel paper (allowed from pending or no). */
export async function recordPaperConsent(motherId: string, consentTextVersion: string, actor?: Actor): Promise<Mother> {
  const yes = { consentStatus: 'yes' as const, consentChannel: 'paper' as const, consentAt: new Date().toISOString(), consentTextVersion, optedOut: false };
  if (isReal()) {
    const fid = scope().facilityId;
    await updateDoc(doc(db!, 'facilities', fid, 'mothers', motherId), { ...yes, optOutReason: deleteField() });
    addAudit(actor, 'patient.consent_paper', `Patient ${motherId}`, fid, consentTextVersion);
    const c = await loadClinic(fid);
    return c.mothers.find((x) => x.id === motherId)!;
  }
  const m = mothers.find((x) => x.id === motherId);
  if (!m) throw new Error('Patient not found');
  Object.assign(m, yes);
  delete m.optOutReason;
  addAudit(actor, 'patient.consent_paper', `Patient ${m.id}`, m.facilityId, consentTextVersion);
  return respond(m);
}

export async function getAttendanceList(facilityId: string, range: AttendanceRange): Promise<DueItem[]> {
  const c = await loadClinic(facilityId);
  const today = startOfToday();
  const to = range === 'today' ? 0 : 6;
  const items = c.mothers
    .flatMap((m) => itemsFor(c, m))
    .filter((i) => {
      const d = dayDiff(today, parseDate(i.date));
      return (d < 0 && i.status !== 'attended') || (d >= -3 && d <= to);
    })
    .sort((a, b) => a.date.localeCompare(b.date));
  return c.real ? items : respond(items);
}

// status null = undo the mark
export async function markAttendance(item: Pick<DueItem, 'id' | 'kind' | 'date'>, status: DoseStatus | null): Promise<void> {
  if (isReal()) {
    const { facilityId: fid, uid } = scope();
    if (item.kind === 'dose') {
      await updateDoc(doc(db!, 'facilities', fid, 'doses', item.id), {
        status: status ?? realStatus(undefined, item.date),
        attendedAt: status === 'attended' ? new Date().toISOString() : null,
        markedBy: uid,
      });
    } else if (status) {
      await setDoc(doc(db!, 'facilities', fid, 'milestoneMarks', item.id), { status, markedBy: uid, at: new Date().toISOString() });
    } else {
      await deleteDoc(doc(db!, 'facilities', fid, 'milestoneMarks', item.id));
    }
    addAudit(scope().actor, status ? `attendance.${status}` : 'attendance.undone', item.id, fid);
    return;
  }
  const reset = statusByDiff(dayDiff(startOfToday(), parseDate(item.date)));
  const restored = reset === 'attended' ? 'due' : reset;
  if (item.kind === 'dose') {
    const d = doses.find((x) => x.id === item.id);
    if (d) d.status = status ?? restored;
  } else {
    milestoneMarks[item.id] = status ?? restored;
  }
  return respond(undefined);
}

/** The mother's own words that raised a danger flag. Doctors and clinic admin only (firestore.rules); others get none. */
export async function getFlagWords(flag: Flag): Promise<string[]> {
  if (!flag.callId) return [];
  if (isReal()) {
    const snap = await getDoc(doc(db!, 'facilities', scope().facilityId, 'flags', flag.id, 'private', 'words')).catch(() => null);
    const lines = snap?.get('lines');
    return Array.isArray(lines) ? lines.filter((x): x is string => typeof x === 'string') : [];
  }
  // Only what she actually said on a call (never the summary or a check-in answer).
  return respond(flag.motherWords ?? []);
}

export async function acknowledgeFlag(flagId: string, by: string): Promise<void> {
  if (isReal()) {
    const { facilityId: fid, uid } = scope();
    await updateDoc(doc(db!, 'facilities', fid, 'flags', flagId), {
      status: 'in_progress',
      acknowledgedAt: new Date().toISOString(),
      acknowledgedBy: uid,
      acknowledgedByName: by,
    });
    addAudit(scope().actor, 'flag.acknowledged', flagId, fid);
    return;
  }
  const f = flags.find((x) => x.id === flagId);
  if (f && f.status === 'open') Object.assign(f, { status: 'in_progress', acknowledgedAt: new Date().toISOString(), acknowledgedBy: by });
  return respond(undefined);
}

/** Mark a flag handled. The note is optional on Today ("Mark handled"); screens that need one check before calling. */
export async function resolveFlag(flagId: string, note: string, by: string): Promise<void> {
  if (isReal()) {
    const { facilityId: fid, uid } = scope();
    const c = await loadClinic(fid);
    const f = c.flags.find((x) => x.id === flagId);
    await updateDoc(doc(db!, 'facilities', fid, 'flags', flagId), {
      status: 'resolved',
      resolvedNote: note.trim(),
      resolvedBy: uid,
      resolvedByName: by,
      resolvedAt: new Date().toISOString(),
      ...(f?.acknowledgedAt ? {} : { acknowledgedAt: new Date().toISOString(), acknowledgedBy: uid, acknowledgedByName: by }),
    });
    addAudit(scope().actor, 'flag.resolved', flagId, fid);
    return;
  }
  const f = flags.find((x) => x.id === flagId);
  if (!f) throw new Error('Flag not found');
  Object.assign(f, { status: 'resolved', resolvedNote: note.trim(), resolvedNoteHi: undefined, resolvedByName: by, resolvedAt: new Date().toISOString(), acknowledgedAt: f.acknowledgedAt ?? new Date().toISOString(), acknowledgedBy: f.acknowledgedBy ?? by });
  return respond(undefined);
}

// Demo only: real card reading (AI) comes later and always needs staff to confirm.
export async function scanCard(_file: File): Promise<ScannedCard> {
  return respond(
    { name: 'Geeta Kashyap', phone: '5550000099', stage: 'baby', dob: toIsoDate(addDays(startOfToday(), -35)), uncertain: ['phone', 'date'] },
    1400,
  );
}

